import { createTheme, type MantineColorsTuple } from "@mantine/core";
import { createElement } from "react";
import { Eye, EyeOff } from "lucide-react";

const VisibilityToggleIcon = ({ reveal }: { reveal: boolean }) =>
  createElement(reveal ? EyeOff : Eye, {
    size: 18,
    strokeWidth: 2,
    color: "var(--mantine-color-gray-6)",
  });

const brand: MantineColorsTuple = [
  "#eef4ff",
  "#dce5f7",
  "#b4c8ee",
  "#8aabe6",
  "#6692de",
  "#4f81d9",
  "#3b82f6",
  "#2563eb",
  "#1d4ed8",
  "#1e40af",
];

const accent: MantineColorsTuple = brand;

const inputStyles = {
  label: {
    fontWeight: 700,
    fontSize: 11,
    color: "var(--mantine-color-brand-9)",
    marginBottom: 8,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
  input: {
    height: 48,
    paddingLeft: 16,
    paddingRight: 16,
    fontSize: 14,
    fontWeight: 500,
    color: "var(--mantine-color-gray-9)",
    background: "var(--mantine-color-gray-0)",
    border: "1px solid transparent",
    transition: "border-color 120ms ease, background 120ms ease, box-shadow 120ms ease",
    "&:focus, &:focusWithin": {
      borderColor: "var(--mantine-color-brand-6)",
      background: "white",
      boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.10)",
    },
    "&::placeholder": {
      color: "var(--mantine-color-gray-5)",
      fontWeight: 400,
    },
  },
  description: {
    fontSize: 12,
    color: "var(--mantine-color-gray-6)",
    marginTop: 4,
  },
  error: {
    fontSize: 12,
    fontWeight: 500,
    marginTop: 4,
  },
};

const textAreaStyles = {
  ...inputStyles,
  input: {
    ...inputStyles.input,
    height: "auto",
    paddingTop: 12,
    paddingBottom: 12,
    lineHeight: 1.5,
  },
};

export const theme = createTheme({
  primaryColor: "brand",
  primaryShade: { light: 7, dark: 6 },
  defaultRadius: "lg",
  fontFamily:
    '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace:
    '"JetBrains Mono", "Fira Code", Consolas, "Courier New", monospace',
  headings: {
    fontFamily:
      '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: "700",
  },
  colors: {
    brand,
    accent,
  },
  shadows: {
    xs: "0 1px 2px rgba(15, 23, 42, 0.04)",
    sm: "0 2px 4px rgba(15, 23, 42, 0.05), 0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04)",
  },
  components: {
    Card: {
      defaultProps: { radius: "xl", withBorder: true, shadow: "xs" },
      styles: { root: { borderColor: "var(--mantine-color-gray-2)" } },
    },
    Button: {
      defaultProps: { radius: "md" },
      styles: { root: { fontWeight: 600, letterSpacing: "0.01em" } },
    },
    TextInput: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    PasswordInput: {
      defaultProps: {
        radius: "md",
        size: "md",
        visibilityToggleIcon: VisibilityToggleIcon,
      },
      styles: { ...inputStyles, innerInput: inputStyles.input },
    },
    NumberInput: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    Select: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    MultiSelect: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    DateInput: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    Textarea: { defaultProps: { radius: "md", size: "md" }, styles: textAreaStyles },
    FileInput: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    TagsInput: { defaultProps: { radius: "md", size: "md" }, styles: inputStyles },
    Modal: {
      defaultProps: { radius: "xl", centered: true, padding: "xl" },
      styles: {
        title: { fontWeight: 700, fontSize: 18 },
        header: { borderBottom: "1px solid var(--mantine-color-gray-2)", marginBottom: 16 },
      },
    },
    Badge: {
      defaultProps: { radius: "sm" },
      styles: { root: { fontWeight: 600, letterSpacing: "0.02em" } },
    },
    Table: {
      styles: {
        th: {
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: "0.04em",
          textTransform: "uppercase" as const,
          color: "var(--mantine-color-brand-9)",
          backgroundColor: "var(--mantine-color-brand-0)",
          padding: "14px 16px",
        },
        td: {
          padding: "16px",
          fontSize: 14,
          borderTop: "1px solid var(--mantine-color-gray-2)",
        },
      },
    },
  },
});
